import React from 'react';
import {
  MousePointer2,
  Pencil,
  Highlighter,
  Minus,
  ArrowUpRight,
  Frame,
  Circle,
  Eraser,
  MessageSquare,
  BringToFront,
  SendToBack,
} from 'lucide-react';
import type { BoardDrawTool } from '../../types';
import { DRAW_COLORS, DRAW_WIDTHS, HIGHLIGHTER_WIDTHS } from './boardConstants';

interface BoardDrawToolbarProps {
  tool: BoardDrawTool;
  color: string;
  strokeWidth: number;
  onToolChange: (tool: BoardDrawTool) => void;
  onColorChange: (color: string) => void;
  onWidthChange: (width: number) => void;
  hasSelection?: boolean;
  onBringToFront?: () => void;
  onSendToBack?: () => void;
}

const TOOLS: { id: BoardDrawTool; label: string; shortcut: string; icon: React.ReactNode }[] = [
  { id: 'select', label: 'Select', shortcut: 'V', icon: <MousePointer2 size={16} /> },
  { id: 'pen', label: 'Pen', shortcut: 'P', icon: <Pencil size={16} /> },
  { id: 'highlighter', label: 'Highlighter', shortcut: 'H', icon: <Highlighter size={16} /> },
  { id: 'line', label: 'Line', shortcut: 'L', icon: <Minus size={16} /> },
  { id: 'arrow', label: 'Arrow', shortcut: 'A', icon: <ArrowUpRight size={16} /> },
  { id: 'rect', label: 'Frame — always the bottom layer', shortcut: 'R', icon: <Frame size={16} /> },
  { id: 'ellipse', label: 'Ellipse', shortcut: 'O', icon: <Circle size={16} /> },
  { id: 'callout', label: 'Callout — pin a note with your initials', shortcut: 'C', icon: <MessageSquare size={16} /> },
  { id: 'eraser', label: 'Eraser', shortcut: 'E', icon: <Eraser size={16} /> },
];

const BoardDrawToolbar: React.FC<BoardDrawToolbarProps> = ({
  tool,
  color,
  strokeWidth,
  onToolChange,
  onColorChange,
  onWidthChange,
  hasSelection,
  onBringToFront,
  onSendToBack,
}) => {
  const widths = tool === 'highlighter' ? HIGHLIGHTER_WIDTHS : DRAW_WIDTHS;
  const showStyle = (tool !== 'select' && tool !== 'eraser') || !!hasSelection;

  return (
    <div className="board-draw-toolbar">
      {TOOLS.map((t) => (
        <button
          key={t.id}
          type="button"
          className={`board-draw-tool${tool === t.id ? ' is-active' : ''}`}
          title={`${t.label} (${t.shortcut})`}
          onClick={() => onToolChange(t.id)}
        >
          {t.icon}
        </button>
      ))}
      {showStyle && (
        <>
          <span className="board-toolbar-sep" />
          <div className="board-draw-swatches">
            {DRAW_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                className={`board-draw-swatch${color === c ? ' is-active' : ''}`}
                style={{ background: c }}
                title={c}
                onClick={() => onColorChange(c)}
              />
            ))}
          </div>
          <span className="board-toolbar-sep" />
          <div className="board-draw-widths">
            {widths.map((w) => (
              <button
                key={w}
                type="button"
                className={`board-draw-width${strokeWidth === w ? ' is-active' : ''}`}
                title={`${w}px`}
                onClick={() => onWidthChange(w)}
              >
                <span style={{ width: Math.min(w + 4, 18), height: Math.min(w + 4, 18) }} />
              </button>
            ))}
          </div>
        </>
      )}
      {hasSelection && (onBringToFront || onSendToBack) && (
        <>
          <span className="board-toolbar-sep" />
          {onBringToFront && (
            <button
              type="button"
              className="board-draw-tool"
              title="Bring to front"
              onClick={onBringToFront}
            >
              <BringToFront size={16} />
            </button>
          )}
          {onSendToBack && (
            <button
              type="button"
              className="board-draw-tool"
              title="Send to back"
              onClick={onSendToBack}
            >
              <SendToBack size={16} />
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default BoardDrawToolbar;
